import React, { useState, useEffect, useContext } from "react";
import { getDisplayName } from "../utils/displayName";
import { AiFillCloseCircle } from "react-icons/ai";
import Select from "react-select";
import Features from "./Steps/Features";
import { UserContext } from "../context/MainContext";
import { useNavigate } from "react-router-dom";
import { hidalgoTaxLookup } from "../actions/taxLookup";
import {
  flooringType,
  roof,
  streetSurface,
  countertops,
  foundationType,
  energyFeatures,
  laundry,
  garageCarport,
  specialFeatures,
  mainSuite,
  exteriorFeatures,
  hvac,
  waterHeater,
  waterSewer,
  poolDetails,
  exteriorSiding,
  interiorFeatures,
  windowCoverings,
  fenceType,
  locationFeatures,
  conveyingAppliances,
  docsOnFile,
  communityAmenities,
} from "../constants/StepsConstants";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import "react-accessible-accordion/dist/fancy-example.css";
import UploadWidget from "./UploadWidget";
import { propertyUpdate } from "../actions/propertyUpdate";
import { TextareaField } from "./InputField";
import { userLenderSearch } from "../actions/userLenderSearch";
import { titleCompanySearch } from "../actions/titleCompanySearch";
import { userRealtorSearch } from "../actions/userRealtorSearch";

// Temporarily disable tax prefill UI and functionality
const TAX_PREFILL_ENABLED = false;

const ModalEditProperty = ({
  modal,
  setModal,
  property,
  setProperty,
  onPropertyUpdated,
}) => {
  const [uploadedItems, setUploadedItems] = useState([]);
  const [features, setFeatures] = useState([]);
  const [initialProperty, setInitialProperty] = useState(null);
  const [initialFeatures, setInitialFeatures] = useState([]);
  const [initialStatus, setInitialStatus] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFinancingDropdown, setShowFinancingDropdown] = useState(false);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    const feats = (property?.feature_list || "").split(",").filter(Boolean);
    setFeatures(feats);
    setInitialFeatures(feats);
    setInitialProperty(JSON.stringify(property));
    setInitialStatus(property?.property_status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.feature_list]);

  const hasUnsavedChanges = () => {
    // Check if property data has changed
    const currentProperty = JSON.stringify(property);
    const propertyChanged = currentProperty !== initialProperty;

    // Check if features have changed
    const featuresChanged =
      JSON.stringify(features.sort()) !==
      JSON.stringify(initialFeatures.sort());

    // Check if files have been uploaded
    const filesUploaded = uploadedItems.length > 0;

    return propertyChanged || featuresChanged || filesUploaded;
  };

  const handleCloseModal = () => {
    if (hasUnsavedChanges()) {
      const confirmClose = window.confirm(
        "You have unsaved changes. Are you sure you want to close without saving?"
      );
      if (confirmClose) {
        setModal(false);
      }
    } else {
      setModal(false);
    }
  };

  const addCommas = (num) => {
    if (!num) return "";
    // Remove non-digit chars
    num = num.replace(/\D/g, "");
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Normalize URLs to ensure they have a proper scheme for video fields
    const videoFields = [
      "embeded_url",
      "embeded_url2",
      "embeded_url3",
      "embeded_url4",
    ];
    if (videoFields.includes(name) && value) {
      const trimmed = value.trim();
      if (trimmed && !/^https?:\/\//i.test(trimmed)) {
        processedValue = `https://${trimmed}`;
      } else {
        processedValue = trimmed;
      }
    }

    // Input restrictions and formatting for numeric fields (minimal for acreage)
    if (
      [
        "zip",
        "street_number",
        "beds_total",
        "acreage",
        "living_sqft",
        "living_areas",
        "est_year_built",
      ].includes(name)
    ) {
      if (name === "acreage") {
        // Accept digits, one decimal separator (dot or comma), and up to 4 fractional digits.
        let cleaned = value.replace(/,/g, ".");
        cleaned = cleaned.replace(/[^0-9.]/g, "");
        const firstDot = cleaned.indexOf(".");
        if (firstDot !== -1) {
          const intPart = cleaned.slice(0, firstDot);
          const frac = cleaned
            .slice(firstDot + 1)
            .replace(/\./g, "")
            .slice(0, 4);
          processedValue = intPart
            ? `${intPart}.${frac}`
            : frac
            ? `.${frac}`
            : "";
        } else {
          processedValue = cleaned;
        }
      } else {
        // existing behavior for integer-only fields
        let digitsOnly = value.replace(/\D/g, "");
        let maxLen = 6;
        let useCommas = false;
        if (name === "zip") maxLen = 5;
        if (name === "street_number") maxLen = 6;
        if (name === "beds_total") maxLen = 2;
        if (name === "living_sqft") {
          maxLen = 6;
          useCommas = true;
        }
        if (name === "living_areas") maxLen = 2;
        if (name === "est_year_built") maxLen = 4;
        digitsOnly = digitsOnly.slice(0, maxLen);
        processedValue = useCommas ? addCommas(digitsOnly) : digitsOnly;
      }
    }

    setProperty({ ...property, [name]: processedValue });
  };

  const financingOptionsList = [
    "Cash",
    "Conventional",
    "FHA",
    "Owner Financing",
    "Other",
    "VA",
  ];

  const handleFinancingToggle = (option) => {
    const current = (property.financing_options || "").toString();
    const items = current ? current.split(",").map((s) => s.trim()) : [];
    const idx = items.indexOf(option);
    if (idx === -1) items.push(option);
    else items.splice(idx, 1);
    handleChange({
      target: { name: "financing_options", value: items.join(", ") },
    });
  };
  const handleCheckbox = (e) => {
    const { name, checked } = e.target;
    if (checked) {
      setFeatures([...new Set([...features, name])]);
    } else {
      setFeatures(features.filter((f) => f !== name));
    }
  };
  const handleUpdate = async () => {
    // Check if property is in a non-editable state
    const nonEditableStatuses = ["Sold", "Leased", "Withdrawn"];
    if (
      initialStatus &&
      nonEditableStatuses.includes(initialStatus) &&
      initialStatus === property["property_status"]
    ) {
      alert(
        `Cannot edit a ${initialStatus} property. Please change status to Active first to make edits.`
      );
      return;
    }

    if (property["property_status"] === "Sold" && !property["sold_for"]) {
      alert("Please enter the sold price before marking as Sold.");
      return;
    }
    if (property["property_status"] === "Leased" && !property["sold_for"]) {
      alert("Please enter the lease amount before marking as Leased.");
      return;
    }

    setIsUpdating(true);

    const mediaToUse =
      uploadedItems.length === 0 ? property.media_url : uploadedItems;

    const featuresToSend = (property?.feature_list || "")
      .split(",")
      .map((s) => s && s.trim())
      .filter(Boolean);

    const result = await propertyUpdate(
      property,
      featuresToSend,
      user?.user_id,
      mediaToUse
    );

    setIsUpdating(false);

    if (result.success) {
      setProperty({ ...property });
      onPropertyUpdated?.();
      setShowSuccessModal(true);
    } else {
      alert(`Failed to update property: ${result.error}`);
    }
  };
  // Related to lender
  const [results, setResults] = useState([]);
  const [lenderSaved, setLenderSaved] = useState(false);
  const handleChoose = (userId) => {
    setProperty({ ...property, lender_used: userId });
  };

  // Related to title company
  const [titleResults, setTitleResults] = useState([]);
  const [titleSaved, setTitleSaved] = useState(false);

  // Related to realtors/agents
  const [realtors, setRealtors] = useState([]);
  const [manualAgent, setManualAgent] = useState(false);

  const [taxLoading, setTaxLoading] = useState(false);
  const [autoError, setAutoError] = useState("");
  const [taxStatus, setTaxStatus] = useState("idle");
  const [taxResult, setTaxResult] = useState(null);
  const [showTaxModal, setShowTaxModal] = useState(false);

  const handleTaxLookup = async () => {
    if (!TAX_PREFILL_ENABLED) return;
    setTaxLoading(true);
    setAutoError("");
    setTaxStatus("looking");
    setShowTaxModal(true);
    try {
      const q = property.parcel_num_geo_tax_id || property.property_tax_number;
      if (!q) throw new Error("Tax ID is empty");
      const { mapped, raw, source } = await hidalgoTaxLookup(q);
      setTaxResult({ mapped, raw, source });
      if (mapped && Object.keys(mapped).length > 0) {
        setTaxStatus("found");
        setTaxStatus("filling");
        const pickNumeric = (obj, keys) => {
          for (const k of keys) {
            if (!obj) continue;
            for (const kk of Object.keys(obj)) {
              if (kk.toLowerCase().includes(k) && obj[kk]) return obj[kk];
            }
          }
          return undefined;
        };
        const parseAddress = (addr) => {
          if (!addr || typeof addr !== "string") return {};
          const parts = addr.split(",").map((p) => p.trim());
          const street = parts[0] || "";
          const cityStateZip = parts[1] || "";
          const cityMatch = cityStateZip.match(/^([^\d,]+?)\s*(\d{5})?$/);
          const city = cityMatch
            ? cityMatch[1].trim()
            : cityStateZip.split(" ")[0] || "";
          const zip = cityMatch && cityMatch[2] ? cityMatch[2] : undefined;
          const m = street.match(/^(\d+)\s+(.*)$/);
          return {
            address: addr,
            street_number: m ? m[1] : undefined,
            street_name: m ? m[2] : undefined,
            city: city || undefined,
            zip: zip || undefined,
          };
        };

        const addrMap = parseAddress(
          mapped.situs_address ||
            mapped.address ||
            mapped.Situs ||
            mapped.situs ||
            ""
        );
        const baths = pickNumeric(mapped, ["bath", "baths"]);

        setProperty({
          ...property,
          legal_description:
            mapped.legal_description || property.legal_description,
          acreage: mapped.acreage || property.acreage,
          est_year_built: mapped.est_year_built || property.est_year_built,
          living_sqft: mapped.living_sqft || property.living_sqft,
          address: addrMap.address || property.address,
          street_number: addrMap.street_number || property.street_number,
          street_name: addrMap.street_name || property.street_name,
          city: addrMap.city || property.city,
          zip: addrMap.zip || property.zip,
          baths_full: baths || property.baths_full,
        });
        setTaxStatus("filled");
        setAutoError("");
      } else {
        setTaxStatus("failed");
        setAutoError("No CAD results found for that Tax ID.");
      }
    } catch (e) {
      setTaxStatus("failed");
      setAutoError("Unable to fetch CAD details.");
    } finally {
      setTaxLoading(false);
    }
  };
  const handleTitleChoose = (userId) => {
    setProperty({ ...property, title_company_used: userId });
  };

  // Load all lenders, title companies, and realtors on mount
  useEffect(() => {
    // Load lenders - search with empty/common names to get all
    userLenderSearch("", "", { setResults });
    // Load title companies
    titleCompanySearch("", { setTitleResults });
    // Load realtors - search with empty names to get all
    userRealtorSearch("", { setRealtors });
  }, []);

  return (
    <>
      <div className=" flex bg-black/40 w-[100%] top-0  left-0 h-screen fixed z-50 "></div>
      <div className="flex justify-center absolute top-64 w-[calc(100vw+100px)]  left-0 tablet:-m-32 -m-6  z-50 ">
        <div className="bg-white flex flex-col p-4 pt-6 px-14 text-center relative w-[800px]">
          <div
            className="absolute right-4 hover:scale-105 hover:cursor-pointer transition-all"
            onClick={handleCloseModal}
          >
            <AiFillCloseCircle size={30} />
          </div>

          {(initialStatus === "Sold" || initialStatus === "Leased") && (
            <div
              className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4"
              role="alert"
            >
              <p className="font-bold">Property is {initialStatus}</p>
              <p className="text-sm">
                This property is marked as {initialStatus}. To make changes, you
                must first change the status to "Active" and save. Only status
                changes are allowed on {initialStatus} properties.
              </p>
            </div>
          )}
          <h1 className="mb-2 text-30">Edit Property</h1>
          <hr />
          <p className="my-2 text-xs">
            Changes will only be made upon pressing UPDATE
          </p>
          <div className="flex flex-col gap-4">
            <p className="text-19 text-left">
              Status of Property -{" "}
              <i className="text-xs">
                If property is for <i className="text-red-500">Sale/Lease</i>{" "}
                please use <i className="text-red-500">Sold/Leased</i> to
                signify that it has been purchased/leased.
              </i>
            </p>
            <select
              className="bg-slate-300 p-[2px] w-[120px]"
              name="property_status"
              value={property["property_status"] || ""}
              onChange={handleChange}
            >
              {property["for"] === "Lease" ? (
                <>
                  <option></option>
                  <option>Active</option>
                  <option>Pending</option>
                  <option>Option</option>
                  <option value="Not Active">Temporarily Off Market</option>
                  <option>Leased</option>
                  <option>Withdrawn</option>
                  <option>Expired</option>
                </>
              ) : (
                <>
                  <option></option>
                  <option>Active</option>
                  <option>Pending</option>
                  <option>Option</option>
                  <option value="Not Active">Temporarily Off Market</option>
                  <option>Sold</option>
                  <option>Withdrawn</option>
                  <option>Expired</option>
                </>
              )}
            </select>
            {/* Listing Dates Section */}
            <hr />
            <p className="text-19 text-left font-semibold">Listing Dates</p>
            <div className="flex flex-col gap-2">
              <label className="text-sm">Listing Start Date</label>
              <input
                className="bg-slate-300 px-2 py-1"
                name="listing_start_date"
                type="date"
                value={
                  property["listing_start_date"]
                    ? property["listing_start_date"].split("T")[0]
                    : ""
                }
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm">Listing Expiration Date</label>
              <input
                className="bg-slate-300 px-2 py-1"
                name="listing_expiration_date"
                type="date"
                value={
                  property["listing_expiration_date"]
                    ? property["listing_expiration_date"].split("T")[0]
                    : ""
                }
                onChange={handleChange}
              />
              <p className="text-xs text-gray-600">
                Property status will automatically change to "Withdrawn" once
                this date is reached.
              </p>
            </div>
            {property["property_status"] === "Sold" && (
              <>
                <hr />
                <p>
                  <b className="text-red-500">WARNING:</b> You will not be able
                  to edit this property after submitting as sold. Please make
                  sure all data is correct.
                </p>
                <div className="flex flex-col">
                  <label>
                    How much was it sold for? No commas or decimals.
                  </label>
                  <input
                    className="bg-slate-300 px-2"
                    name="sold_for"
                    type="text"
                    value={property["sold_for"] || ""}
                    onChange={handleChange}
                  ></input>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="font-semibold">Selling Agent</label>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!manualAgent}
                        onChange={() => setManualAgent(false)}
                      />
                      <span className="text-sm">Select from system</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={manualAgent}
                        onChange={() => setManualAgent(true)}
                      />
                      <span className="text-sm">Enter manually</span>
                    </label>
                  </div>

                  {!manualAgent ? (
                    <select
                      className="bg-slate-300 px-2 py-1"
                      name="selling_agent"
                      value={property["selling_agent"] || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select an agent...</option>
                      {realtors.map((realtor) => (
                        <option
                          key={realtor.user_id}
                          value={`${realtor.brokerage_name || "N/A"} - ${
                            realtor.legal_last_name
                          }, ${realtor.legal_first_name}`}
                        >
                          {realtor.brokerage_name || "N/A"} -{" "}
                          {getDisplayName(realtor)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <input
                        className="bg-slate-300 px-2 py-1"
                        name="selling_agent_brokerage"
                        type="text"
                        placeholder="Brokerage Name"
                        value={property["selling_agent_brokerage"] || ""}
                        onChange={handleChange}
                      />
                      <input
                        className="bg-slate-300 px-2 py-1"
                        name="selling_agent_name"
                        type="text"
                        placeholder="Agent Full Name"
                        value={property["selling_agent_name"] || ""}
                        onChange={handleChange}
                      />
                      <input
                        className="bg-slate-300 px-2 py-1"
                        name="selling_agent_license"
                        type="text"
                        placeholder="License Number"
                        value={property["selling_agent_license"] || ""}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                </div>
                <div className="flex flex-col w-min">
                  <label>Closing Date</label>
                  <input
                    className="bg-slate-300 px-2"
                    name="closing_date"
                    type="date"
                    value={property["closing_date"] || ""}
                    onChange={handleChange}
                  ></input>
                </div>

                <div className="flex flex-col w-max">
                  <label>Repairs Y/N</label>
                  <select
                    className="bg-slate-300 px-2"
                    name="repairs"
                    type="text"
                    value={property["repairs"] || ""}
                    onChange={handleChange}
                  >
                    <option></option>
                    <option>Yes</option>
                    <option>No</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <label>Seller Concessions - No commas or decimals.</label>
                  <input
                    className="bg-slate-300 px-2"
                    name="sold_for"
                    type="text"
                    value={property["seller_concessions"] || ""}
                    onChange={handleChange}
                  ></input>
                </div>
                <hr />
              </>
            )}
            {property["property_status"] === "Leased" && (
              <>
                <hr />
                <p>
                  <b className="text-red-500">WARNING:</b> You will not be able
                  to edit this property after submitting as leased. Please make
                  sure all data is correct.
                </p>
                <div className="flex flex-col">
                  <label>
                    Lease Amount (Monthly Rent)? No commas or decimals.
                  </label>
                  <input
                    className="bg-slate-300 px-2"
                    name="sold_for"
                    type="text"
                    value={property["sold_for"] || ""}
                    onChange={handleChange}
                  ></input>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="font-semibold">
                    Brokerage / Leasing Agent
                  </label>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!manualAgent}
                        onChange={() => setManualAgent(false)}
                      />
                      <span className="text-sm">Select from system</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={manualAgent}
                        onChange={() => setManualAgent(true)}
                      />
                      <span className="text-sm">Enter manually</span>
                    </label>
                  </div>

                  {!manualAgent ? (
                    <select
                      className="bg-slate-300 px-2 py-1"
                      name="selling_agent"
                      value={property["selling_agent"] || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select an agent...</option>
                      {realtors.map((realtor) => (
                        <option
                          key={realtor.user_id}
                          value={`${realtor.brokerage_name || "N/A"} - ${
                            realtor.legal_last_name
                          }, ${realtor.legal_first_name}`}
                        >
                          {realtor.brokerage_name || "N/A"} -{" "}
                          {realtor.legal_last_name}, {realtor.legal_first_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <input
                        className="bg-slate-300 px-2 py-1"
                        name="selling_agent_brokerage"
                        type="text"
                        placeholder="Brokerage Name"
                        value={property["selling_agent_brokerage"] || ""}
                        onChange={handleChange}
                      />
                      <input
                        className="bg-slate-300 px-2 py-1"
                        name="selling_agent_name"
                        type="text"
                        placeholder="Agent Full Name"
                        value={property["selling_agent_name"] || ""}
                        onChange={handleChange}
                      />
                      <input
                        className="bg-slate-300 px-2 py-1"
                        name="selling_agent_license"
                        type="text"
                        placeholder="License Number"
                        value={property["selling_agent_license"] || ""}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                </div>
                <div className="flex flex-col w-min">
                  <label>Lease Start Date</label>
                  <input
                    className="bg-slate-300 px-2"
                    name="closing_date"
                    type="date"
                    value={property["closing_date"] || ""}
                    onChange={handleChange}
                  ></input>
                </div>
                <hr />
              </>
            )}
            <h1 className="text-30 font-bold text-left">Property</h1>
            <Accordion
              className="flex flex-col gap-6"
              allowMultipleExpanded={true}
              allowZeroExpanded={false}
            >
              {/* Property Information section - hidden but kept for data integrity */}
              {false && (
                <AccordionItem>
                  <AccordionItemHeading className="text-19">
                    <AccordionItemButton>
                      Property Information
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <div className="flex flex-col gap-6">
                      <div className="flex flex-wrap justify-center gap-6 items-end">
                        <div className="flex flex-col">
                          <label>Property Sub Type</label>
                          <select
                            className="bg-slate-300 p-[2px] w-[120px]"
                            name="sub_type"
                            value={property["sub_type"] || ""}
                            onChange={handleChange}
                          >
                            <option></option>
                            <option>Apartment</option>
                            <option>Condo</option>
                            <option>Single Family</option>
                            <option>Multi-Family</option>
                            <option>Town Home</option>
                          </select>
                        </div>
                        <div className="flex flex-col">
                          <label>City</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="city"
                            type="text"
                            value={property["city"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        <div className="flex flex-col">
                          <label>Zip</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="zip"
                            type="text"
                            value={property["zip"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        <div className="flex flex-col">
                          <label>County</label>
                          <select
                            className="bg-slate-300 p-[2px] w-[120px]"
                            name="county"
                            value={property["county"] || ""}
                            onChange={handleChange}
                          >
                            <option></option>
                            <option>Hidalgo</option>
                            <option>Cameron</option>
                            <option>Willacy</option>
                            <option>Starr</option>
                          </select>
                        </div>
                      </div>
                      {TAX_PREFILL_ENABLED && (
                        <div className="flex flex-col gap-2">
                          <label>Property Tax ID #</label>
                          <input
                            className="bg-slate-300 px-2 resize-y"
                            type="text"
                            name="parcel_num_geo_tax_id"
                            value={property["parcel_num_geo_tax_id"] || ""}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleTaxLookup();
                            }}
                          ></input>
                          <button
                            type="button"
                            className="bg-black text-white px-3 py-2 w-max hover:scale-105 disabled:opacity-50"
                            onClick={handleTaxLookup}
                            disabled={taxLoading}
                          >
                            {taxLoading ? "Fetching CAD…" : "Fetch from CAD"}
                          </button>
                          {autoError && (
                            <p className="text-xs text-red-600">{autoError}</p>
                          )}
                          {/* Tax lookup status modal */}
                          {showTaxModal && (
                            <div className="fixed inset-0 flex items-center justify-center z-50">
                              <div
                                className="absolute inset-0 bg-black/40"
                                onClick={() => setShowTaxModal(false)}
                              />
                              <div className="bg-white rounded p-6 z-60 w-[90%] max-w-md">
                                <h3 className="font-bold mb-2">
                                  Property Lookup
                                </h3>
                                <p className="mb-2">
                                  {taxStatus === "looking" &&
                                    "Looking up property..."}
                                  {taxStatus === "found" && "Found results."}
                                  {taxStatus === "filling" &&
                                    "Filling in property fields..."}
                                  {taxStatus === "filled" && "Fields updated."}
                                  {taxStatus === "failed" && "Lookup failed."}
                                </p>
                                {taxResult?.mapped && (
                                  <div className="text-xs text-slate-700 mb-2">
                                    <p className="font-semibold">
                                      Mapped Fields:
                                    </p>
                                    <ul className="list-disc pl-5">
                                      {Object.entries(taxResult.mapped).map(
                                        ([k, v]) => (
                                          <li key={k}>
                                            <b>{k}:</b>{" "}
                                            {String(v).slice(0, 120)}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                                {taxResult?.raw && (
                                  <details className="text-xs text-slate-500 mt-2">
                                    <summary>Show raw response</summary>
                                    <pre className="whitespace-pre-wrap max-h-40 overflow-auto mt-2">
                                      {String(taxResult.raw).slice(0, 10000)}
                                    </pre>
                                  </details>
                                )}
                                <div className="flex justify-end mt-4">
                                  <button
                                    className="px-3 py-1 bg-slate-200 rounded"
                                    onClick={() => setShowTaxModal(false)}
                                  >
                                    Close
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex flex-col">
                        <label>Legal Description</label>
                        <TextareaField
                          className="bg-slate-300 px-2 resize-y"
                          name="legal_description"
                          value={property["legal_description"] || ""}
                          onChange={handleChange}
                          label="Legal Description"
                        />
                      </div>

                      <div className="flex flex-wrap justify-center gap-6">
                        <div className="flex flex-col">
                          <label>Street Number</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="street_number"
                            type="text"
                            value={property["street_number"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        <div className="flex flex-col">
                          <label>Prefix</label>
                          <select
                            className="bg-slate-300 p-[2px] w-[120px]"
                            name="dir_prefix"
                            value={property["dir_prefix"] || ""}
                            onChange={handleChange}
                          >
                            <option></option>

                            <option>E</option>
                            <option>N</option>
                            <option>NE</option>
                            <option>NW</option>
                            <option>S</option>
                            <option>SE</option>
                            <option>SE</option>
                            <option>SW</option>
                            <option>W</option>
                          </select>
                        </div>
                        <div className="flex flex-col">
                          <label>Street Name</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="street_name"
                            type="text"
                            value={property["street_name"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        <div className="flex flex-col">
                          <label>Suffix</label>
                          <select
                            className="bg-slate-300 p-[2px] w-[120px]"
                            name="dir_suffix"
                            value={property["dir_suffix"] || ""}
                            onChange={handleChange}
                          >
                            <option></option>

                            <option>E</option>
                            <option>N</option>
                            <option>NE</option>
                            <option>NW</option>
                            <option>S</option>
                            <option>SE</option>
                            <option>SE</option>
                            <option>SW</option>
                            <option>W</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-center gap-6">
                        <div className="flex flex-col">
                          <label>Street Type</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="street_type"
                            type="text"
                            value={property["street_type"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        <div className="flex flex-col">
                          <label>Unit Number</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="unit_number"
                            type="text"
                            value={property["unit_number"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        <div className="flex flex-col">
                          <label>Subdivision</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="subdivision"
                            type="text"
                            value={property["subdivision"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        <div className="flex flex-col">
                          <label>
                            Residential Subtype (for Residential listings)
                          </label>
                          <select
                            className="bg-slate-300 p-[2px] w-[200px]"
                            name="residentialSubtype"
                            value={property["residentialSubtype"] || ""}
                            onChange={handleChange}
                          >
                            <option value=""></option>
                            <option>Single Family</option>
                            <option>Multi-Family</option>
                            <option>Apartment/Condo</option>
                            <option>Other</option>
                          </select>
                        </div>
                        <div className="flex flex-col">
                          <label className="flex gap-2 items-center">
                            {property["for"] === "Lease"
                              ? "Price/Month"
                              : "Price"}{" "}
                            <p className="text-xs italic">
                              do not include commas, use format: 430000.30
                            </p>
                          </label>
                          <input
                            className="bg-slate-300 px-2"
                            name="price"
                            type="text"
                            value={property["price"] || ""}
                            onChange={handleChange}
                          ></input>
                          {property["for"] === "Sale" &&
                            (property["type"] === "Residential" ||
                              property["type"] === "Commercial") && (
                              <div className="mt-2 relative">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowFinancingDropdown((s) => !s)
                                  }
                                  className="px-3 py-2 bg-white border rounded-md text-sm flex items-center gap-2"
                                >
                                  <span>Financing Options</span>
                                  <span className="text-xs text-gray-500">
                                    {(property.financing_options || "")
                                      .split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean)
                                      .join(", ")}
                                  </span>
                                </button>
                                {showFinancingDropdown && (
                                  <div className="absolute z-20 mt-2 bg-white border rounded-md p-3 shadow-lg">
                                    {financingOptionsList.map((opt) => (
                                      <label
                                        key={opt}
                                        className="flex items-center gap-2 text-sm mb-2"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={(
                                            property.financing_options || ""
                                          )
                                            .split(",")
                                            .map((s) => s.trim())
                                            .includes(opt)}
                                          onChange={() =>
                                            handleFinancingToggle(opt)
                                          }
                                        />
                                        {opt}
                                      </label>
                                    ))}
                                    <div className="text-xs text-gray-500 mt-1">
                                      Shown on the listing next to the price.
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                        </div>
                        {/* CSO removed */}
                      </div>
                      {/* Lease-specific and general options */}
                      <div className="flex flex-col gap-4 mt-2">
                        {property["for"] === "Lease" &&
                          (property["type"] === "Commercial" ||
                            property["type"] === "Residential") && (
                            <div className="flex flex-col">
                              <label>Security Deposit</label>
                              <input
                                className="bg-slate-300 px-2"
                                name="security_deposit"
                                type="text"
                                value={property["security_deposit"] || ""}
                                onChange={handleChange}
                              />
                            </div>
                          )}
                      </div>
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
              )}
              {!["Sold", "Withdrawn", "Expired", "Leased"].includes(
                property?.property_status
              ) && (
                <AccordionItem>
                  <AccordionItemHeading className="text-19">
                    <AccordionItemButton>Property Details</AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <div className="flex flex-col gap-6">
                      <div className="flex flex-wrap justify-center gap-6">
                        <div className="flex flex-col">
                          <label>Beds Total</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="beds_total"
                            type="text"
                            value={property["beds_total"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        <div className="flex flex-col">
                          <label>Baths</label>
                          <select
                            className="bg-slate-300 p-[2px] w-[120px]"
                            name="baths_full"
                            value={property["baths_full"] || ""}
                            onChange={handleChange}
                          >
                            <option></option>
                            <option>1/2 Baths</option>
                            <option>1 1/2 Baths</option>
                            <option>2 Baths</option>
                            <option>2 1/2 Baths</option>
                            <option>3 Baths</option>
                            <option>3 1/2 Baths</option>
                            <option>4 Baths</option>
                            <option>4 1/2 Baths</option>
                            <option>5 Baths</option>
                            <option>5 1/2 Baths</option>
                            <option>6 Baths</option>
                            <option>6 1/2 Baths</option>
                            <option>7 Baths</option>
                            <option>7 1/2 Baths</option>
                            <option>8 Baths</option>
                            <option>8 1/2 Baths</option>
                            <option>9 Baths</option>
                            <option>9 1/2 Baths</option>
                            <option>10 Baths</option>
                            <option>10 1/2 Baths</option>
                            <option>11 Baths</option>
                            <option>11 1/2 Baths</option>
                            <option>12 Baths</option>
                          </select>
                        </div>
                        <div className="flex flex-col">
                          <label>Dining Area</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="dining_area"
                            type="text"
                            value={property["dining_area"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-center gap-6">
                        <div className="flex flex-col">
                          <label>Acreage</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="acreage"
                            type="text"
                            inputMode="decimal"
                            pattern="^(?:\\d{1,6}(?:\\.\\d{0,4})?|\\.\\d{1,4})$"
                            value={property["acreage"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        {/* <div className="flex flex-col">
                                    <label>Lot SqFt</label>
                                    <input className="bg-slate-300 px-2" name="lot_sqft" type="text" value={property["lot_sqft"] || ""} onChange={handleChange}></input>
                            </div>
                            <div className="flex flex-col">
                                    <label>Lot Dimensions</label>
                                    <input className="bg-slate-300 px-2" name="lot_dimensions" type="text" value={property["lot_dimensions"] || ""} onChange={handleChange}></input>
                            </div> */}
                        <div className="flex flex-col">
                          <label>Living SqFt</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="living_sqft"
                            type="text"
                            value={property["living_sqft"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-center gap-6">
                        {/* <div className="flex flex-col">
                                <label>Foyer</label>
                                <select className="bg-slate-300 p-[2px] w-[120px]" name="foyer" value={property["foyer"] || ""} onChange={handleChange}>
                                    <option></option>
                                    <option>Yes</option>
                                    <option>No</option>
                                </select>
                            </div> */}
                        <div className="flex flex-col">
                          <label>Living Areas</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="living_areas"
                            type="text"
                            value={property["living_areas"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        {/* <div className="flex flex-col">
                                    <label>Maid Qtrs Y/N</label>
                                    <select className="bg-slate-300 p-[2px] w-[120px]" name="maid_qtrs" value={property["maid_qtrs"] || ""} onChange={handleChange}>
                                        <option></option>
                                        <option>Yes</option>
                                        <option>No</option>
                                    </select>
                            </div> */}
                        <div className="flex flex-col">
                          <label>SqFt Source</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="sqft_source"
                            type="text"
                            value={property["sqft_source"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-center gap-6">
                        <div className="flex flex-col">
                          <label># Stories</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="stories"
                            type="text"
                            value={property["stories"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        {/* <div className="flex flex-col">
                                    <label>Parcel # / GEO Tax ID</label>
                                    <input className="bg-slate-300 px-2" name="parcel_num_geo_tax_id" type="text" value={property["parcel_num_geo_tax_id"] || ""} onChange={handleChange}></input>
                            </div> */}
                        {/* <div className="flex flex-col">
                                    <label>Taxes WO Exemption</label>
                                    <input className="bg-slate-300 px-2" name="taxes_wo_exemption" type="text" value={property["taxes_wo_exemption"] || ""} onChange={handleChange}></input>
                            </div>
                            <div className="flex flex-col">
                                    <label>Tax Year</label>
                                    <input className="bg-slate-300 px-2" name="tax_year" type="text" value={property["tax_year"] || ""} onChange={handleChange}></input>
                            </div> */}
                      </div>
                      <div className="flex flex-wrap justify-center gap-6">
                        {/* <div className="flex flex-col">
                                    <label>POA Y/N</label>
                                    <select className="bg-slate-300 p-[2px] w-[120px]" name="poa" value={property["poa"] || ""} onChange={handleChange}>
                                        <option></option>
                                        <option>Yes</option>
                                        <option>No</option>
                                    </select>
                            </div> */}
                        <div className="flex flex-col">
                          <label>Est Year Built</label>
                          <input
                            className="bg-slate-300 px-2"
                            name="est_year_built"
                            type="text"
                            value={property["est_year_built"] || ""}
                            onChange={handleChange}
                          ></input>
                        </div>
                        {/* <div className="flex flex-col">
                                    <label>Year Built Source</label>
                                    <select className="bg-slate-300 p-[2px] w-[120px]" name="year_built_source" value={property["year_built_source"] || ""} onChange={handleChange}>
                                        <option></option>
                                        <option>Unavailable</option>
                                    </select>
                            </div> */}
                      </div>
                      <div className="flex flex-wrap justify-center gap-6">
                        {/* <div className="flex flex-col flex-[0.5] ">
                                <label>Legal Description</label>
                                <textarea className="bg-slate-300 px-2 h-[100px] w-full" name="legal_description" type="text" value={property["legal_description"] || ""} onChange={handleChange}></textarea>
                            </div> */}
                      </div>
                      <div className="flex flex-wrap justify-center gap-6">
                        {/* <div className="flex flex-col">
                                    <label>Mobile Home Length</label>
                                    <input className="bg-slate-300 px-2" name="mobile_home_length" type="text" value={property["mobile_home_length"] || ""} onChange={handleChange}></input>
                            </div>
                            <div className="flex flex-col">
                                    <label>Mobile Home Width</label>
                                    <input className="bg-slate-300 px-2" name="mobile_home_width" type="text" value={property["mobile_home_width"] || ""} onChange={handleChange}></input>
                            </div>
                            <div className="flex flex-col">
                                    <label>Mobile Home Serial</label>
                                    <input className="bg-slate-300 px-2" name="mobile_home_serial" type="text" value={property["mobile_home_serial"] || ""} onChange={handleChange}></input>
                            </div>
                            <div className="flex flex-col">
                                    <label>Own Lot Y/N</label>
                                    <select className="bg-slate-300 p-[2px] w-[120px]" name="own_lot" value={property["own_lot"] || ""} onChange={handleChange}>
                                        <option></option>
                                        <option>Yes</option>
                                        <option>No</option>
                                    </select>
                            </div> */}
                      </div>
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
              )}

              {/* Residential Details Section */}
              {property?.type === "Residential" && (
                <AccordionItem>
                  <AccordionItemHeading className="text-19">
                    <AccordionItemButton>
                      Residential Details
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <div className="flex flex-col gap-6">
                      <div className="flex flex-wrap justify-center gap-6">
                        <div className="flex flex-col">
                          <label>Residential Subtype</label>
                          <select
                            className="bg-slate-300 p-[2px] w-[200px]"
                            name="residentialSubtype"
                            value={property["residentialSubtype"] || ""}
                            onChange={handleChange}
                          >
                            <option value=""></option>
                            <option>Single Family</option>
                            <option>Multi-Family</option>
                            <option>Apartment/Condo</option>
                            <option>Other</option>
                          </select>
                        </div>
                        {(property["for"] === "Sale" ||
                          property["for"] === "Lease") && (
                          <>
                            <div className="flex flex-col">
                              <label>School District</label>
                              <input
                                className="bg-slate-300 px-2"
                                name="school_district"
                                type="text"
                                value={property["school_district"] || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="flex flex-col">
                              <label>Elementary School</label>
                              <input
                                className="bg-slate-300 px-2"
                                name="elementary_school"
                                type="text"
                                value={property["elementary_school"] || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="flex flex-col">
                              <label>Middle School</label>
                              <input
                                className="bg-slate-300 px-2"
                                name="middle_school"
                                type="text"
                                value={property["middle_school"] || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="flex flex-col">
                              <label>High School</label>
                              <input
                                className="bg-slate-300 px-2"
                                name="high_school"
                                type="text"
                                value={property["high_school"] || ""}
                                onChange={handleChange}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
              )}
            </Accordion>
            {/* Property Features section */}
            <>
              <h1 className="text-30 font-bold text-left">Property Features</h1>
              <div className="mb-4">
                <Features
                  propertyData={property}
                  setPropertyData={setProperty}
                />
              </div>
            </>
            {property["for"] === "Sale" && (
              <>
                <h1 className="text-left text-30 font-bold">
                  Transaction Details (Optional)
                </h1>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col text-left">
                    <label className="font-bold">
                      Link a preferred lending company / lender
                    </label>
                    <p className="text-xs text-gray-600 mb-2">
                      Choose from dropdown or enter in the field below.
                    </p>
                    <select
                      className="bg-slate-300 px-4 py-2 w-full"
                      value={property["lender_used"] || ""}
                      onChange={(e) => handleChoose(e.target.value)}
                    >
                      <option value="">
                        Choose lender to be linked to this property listing
                      </option>
                      {results &&
                        results.map((result) => (
                          <option key={result.user_id} value={result.user_id}>
                            {result.brokerage_name || "N/A"} -{" "}
                            {getDisplayName(result)}
                          </option>
                        ))}
                    </select>
                    <div className="mt-2">
                      <input
                        className="bg-slate-300 px-4 py-2 w-full"
                        type="text"
                        placeholder="Enter lender company or contact name"
                        value={property["lender_text"] || ""}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: "lender_text",
                              value: e.target.value,
                            },
                          })
                        }
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          className="px-3 py-1 bg-blue-600 text-white rounded"
                          onClick={() => {
                            // Persist the typed lender text onto the property object
                            // and clear any linked lender selection so custom text is used
                            setProperty((prev) => ({
                              ...prev,
                              lender_text: prev.lender_text || "",
                              lender_used: "",
                            }));
                            setLenderSaved(true);
                            setTimeout(() => setLenderSaved(false), 2500);
                          }}
                        >
                          Save
                        </button>
                        {lenderSaved && (
                          <span className="text-sm text-green-600">Saved</span>
                        )}
                      </div>
                    </div>
                    {property["lender_used"] && (
                      <p className="text-sm font-bold mt-2">
                        Lending company / lender chosen for this property
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col text-left">
                    <label className="font-bold">
                      Link a preferred title company / closer
                    </label>
                    <p className="text-xs text-gray-600 mb-2">
                      Choose from dropdown or enter in the field below.
                    </p>
                    <select
                      className="bg-slate-300 px-4 py-2 w-full"
                      value={property["title_company_used"] || ""}
                      onChange={(e) => handleTitleChoose(e.target.value)}
                    >
                      <option value="">
                        Choose title company to be linked to this property
                        listing
                      </option>
                      {titleResults &&
                        titleResults.map((result) => (
                          <option key={result.user_id} value={result.user_id}>
                            {result.brokerage_name || "N/A"} -{" "}
                            {getDisplayName(result)}
                          </option>
                        ))}
                    </select>
                    <div className="mt-2">
                      <input
                        className="bg-slate-300 px-4 py-2 w-full"
                        type="text"
                        placeholder="Enter title company or closer name"
                        value={property["title_company_text"] || ""}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: "title_company_text",
                              value: e.target.value,
                            },
                          })
                        }
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          className="px-3 py-1 bg-blue-600 text-white rounded"
                          onClick={() => {
                            // Persist typed title company text and clear linked selection
                            setProperty((prev) => ({
                              ...prev,
                              title_company_text: prev.title_company_text || "",
                              title_company_used: "",
                            }));
                            setTitleSaved(true);
                            setTimeout(() => setTitleSaved(false), 2500);
                          }}
                        >
                          Save
                        </button>
                        {titleSaved && (
                          <span className="text-sm text-green-600">Saved</span>
                        )}
                      </div>
                    </div>
                    {property["title_company_used"] && (
                      <p className="text-sm font-bold mt-2">
                        Title Company / Closer for this property
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
            {property["property_status"] !== "Leased" && (
              <>
                <h1 className="text-left text-30 font-bold">Upload Images</h1>
                <p className=" text-xs">
                  Any previous images uploaded will be removed, upload all
                  images you wish your property to display.
                </p>
                <UploadWidget
                  setUploadedItems={setUploadedItems}
                  userId={user?.user_id}
                  propertyId={property?.property_id}
                />

                {/* Featured Videos Section - Up to 4 */}
                <div className="flex flex-col bg-slate-50 border-2 border-slate-200 rounded-xl p-4 space-y-4 mt-4">
                  <div>
                    <label className="text-left text-lg font-semibold text-slate-900 mb-2 block">
                      Featured Videos (Up to 4)
                    </label>
                    <p className="text-left text-xs text-slate-600 mb-4">
                      Paste any valid URL to a video. Accepts links from{" "}
                      <strong>
                        TikTok, Instagram, Facebook, YouTube, Vimeo
                      </strong>
                      , or any other platform. Must be a valid public URL.
                    </p>
                  </div>

                  {/* Video 1 */}
                  <div className="flex flex-col">
                    <label className="text-left text-sm font-semibold text-slate-700 mb-2">
                      Video 1 (Primary)
                    </label>
                    <input
                      className="bg-white px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      name="embeded_url"
                      type="text"
                      placeholder="https://www.youtube.com/... or https://www.tiktok.com/..."
                      value={property["embeded_url"] || ""}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Video 2 */}
                  <div className="flex flex-col">
                    <label className="text-left text-sm font-semibold text-slate-700 mb-2">
                      Video 2 (Optional)
                    </label>
                    <input
                      className="bg-white px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      name="embeded_url2"
                      type="text"
                      placeholder="https://www.youtube.com/... or https://www.tiktok.com/..."
                      value={property["embeded_url2"] || ""}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Video 3 */}
                  <div className="flex flex-col">
                    <label className="text-left text-sm font-semibold text-slate-700 mb-2">
                      Video 3 (Optional)
                    </label>
                    <input
                      className="bg-white px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      name="embeded_url3"
                      type="text"
                      placeholder="https://www.youtube.com/... or https://www.tiktok.com/..."
                      value={property["embeded_url3"] || ""}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Video 4 */}
                  <div className="flex flex-col">
                    <label className="text-left text-sm font-semibold text-slate-700 mb-2">
                      Video 4 (Optional)
                    </label>
                    <input
                      className="bg-white px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      name="embeded_url4"
                      type="text"
                      placeholder="https://www.youtube.com/... or https://www.tiktok.com/..."
                      value={property["embeded_url4"] || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </>
            )}
            <button
              className="bg-black hover:scale-105 hover:cursor-pointer transition-all text-white py-2 mt-8"
              onClick={() => handleUpdate()}
            >
              Update
            </button>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {isUpdating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
              <h2 className="text-xl font-bold text-gray-800">
                Updating Property...
              </h2>
              <p className="text-gray-600 text-center">
                Please wait while we save your changes
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <div className="flex flex-col items-center gap-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">
                Property Updated!
              </h2>
              <p className="text-gray-600 text-center">
                Your property has been successfully updated.
              </p>
              <div className="flex gap-4 w-full">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setModal(false);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-all font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setModal(false);
                    navigate(`/property/${property.property_id}`);
                    window.location.reload(); // Refresh to show updated data
                  }}
                  className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all font-medium"
                >
                  View Property
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModalEditProperty;
